import { useCallback, memo, SyntheticEvent } from 'react';
import {
  Panel, PanelHeader, Tabs, Tab, useTranslations, RenderIf,
} from '@uniqkey-frontend/shared-app';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PageRouteEnum from '../../../../enums/PageRouteEnum';
import { publishTablePreservationReset } from '../../../../helpers/tablePreservation/reset';
import {
  useGetOrganizationById,
  useGetOrganizationEmployeeAccountById,
  useGetPartnerById,
} from '../../../../hooks/reactQuery';
import ACLEnum from '../../../../enums/ACLEnum';
import { useUser } from '../../../../contexts/UserContext';
import OrganizationEmployeeDetailsTab
  from '../../../OrganizationEmployeePage/components/OrganizationEmployeeDetailsTab';
import OrganizationEmployeeLoginsTab
  from '../../../OrganizationEmployeePage/components/OrganizationEmployeeLoginsTab';
import OrganizationEmployeeAuditLogsTab
  from '../../../OrganizationEmployeePage/components/OrganizationEmployeeAuditLogsTab';

export enum PartnerOrganizationEmployeeModuleTabEnum {
  Details = 'details',
  Logins = 'logins',
  AuditLogs = 'audit',
}
interface IPartnerOrganizationEmployeeModuleProps {
  activeTab: PartnerOrganizationEmployeeModuleTabEnum;
}
interface IPartnerOrganizationEmployeeModuleContext {
  partnerId: string;
  organizationId: string;
  employeeAccountId: string;
}

const PartnerOrganizationEmployeeModule = (props: IPartnerOrganizationEmployeeModuleProps) => {
  const { activeTab } = props;
  const { userCan } = useUser();
  const { t } = useTranslations();
  const {
    partnerId,
    organizationId,
    employeeAccountId,
  } = useOutletContext<IPartnerOrganizationEmployeeModuleContext>();
  const navigate = useNavigate();

  const { isLoading: isPartnerLoading } = useGetPartnerById(
    { partnerId },
  );

  const { isLoading: isOrganizationLoading } = useGetOrganizationById(
    { organizationId },
  );

  const { isLoading: isEmployeeLoading } = useGetOrganizationEmployeeAccountById(
    { employeeAccountId },
  );

  const isLoading = isPartnerLoading || isOrganizationLoading || isEmployeeLoading;

  const handleTabClick = useCallback((
    event: SyntheticEvent,
    nextActiveTab: PartnerOrganizationEmployeeModuleTabEnum,
  ) => {
    const nextPath = `${
      PageRouteEnum.Partners
    }/${partnerId}/${organizationId}/${employeeAccountId}/${nextActiveTab}`;
    if (activeTab !== nextActiveTab) {
      navigate(nextPath);
      return;
    }
    navigate(nextPath, { replace: true });
    publishTablePreservationReset();
  }, [partnerId, organizationId, employeeAccountId, activeTab, navigate]);

  const hidePanelHeaderBottomDivider = (
    activeTab === PartnerOrganizationEmployeeModuleTabEnum.Details
  );

  const canViewLogins = userCan(ACLEnum.EmployeeAccountViewVaultPasswords);
  const canViewAuditLogs = userCan(ACLEnum.EmployeeAccountViewAuditLogs);

  return (
    <Panel className="height-100-percent flex-column">
      <PanelHeader px={3} hideBottomDivider={hidePanelHeaderBottomDivider}>
        <Tabs value={activeTab} isLoading={isLoading}>
          <Tab
            label={t('organizationEmployeePage.tabs.employeeDetails')}
            value={PartnerOrganizationEmployeeModuleTabEnum.Details}
            onClick={handleTabClick}
          />
          {canViewLogins && (
            <Tab
              label={t('organizationEmployeePage.tabs.logins')}
              value={PartnerOrganizationEmployeeModuleTabEnum.Logins}
              onClick={handleTabClick}
            />
          )}
          {canViewAuditLogs && (
            <Tab
              label={t('organizationEmployeePage.tabs.auditLogs')}
              value={PartnerOrganizationEmployeeModuleTabEnum.AuditLogs}
              onClick={handleTabClick}
            />
          )}
        </Tabs>
      </PanelHeader>
      <RenderIf condition={activeTab === PartnerOrganizationEmployeeModuleTabEnum.Details}>
        <OrganizationEmployeeDetailsTab
          employeeAccountId={employeeAccountId}
          organizationId={organizationId}
        />
      </RenderIf>
      <RenderIf
        condition={canViewLogins && activeTab === PartnerOrganizationEmployeeModuleTabEnum.Logins}
      >
        <OrganizationEmployeeLoginsTab employeeAccountId={employeeAccountId} />
      </RenderIf>
      <RenderIf
        condition={
          canViewAuditLogs && activeTab === PartnerOrganizationEmployeeModuleTabEnum.AuditLogs
        }
      >
        <OrganizationEmployeeAuditLogsTab employeeAccountId={employeeAccountId} />
      </RenderIf>
    </Panel>
  );
};

export default memo(PartnerOrganizationEmployeeModule);
